import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';

const s = {
  page: { display: 'flex', flexDirection: 'column', gap: 24, padding: '24px 40px', background: '#0f172a', minHeight: '100vh', color: '#f8fafc' },
  title: { margin: 0, fontSize: 32, fontWeight: 700, color: '#fff' },
  section: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' },
  sectionTitle: { margin: '0 0 16px', fontSize: 20, color: '#e2e8f0' },
  label: { display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600, color: '#94a3b8' },
  input: { width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #475569', background: '#0f172a', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 16, boxSizing: 'border-box' },
  btnPrimary: { padding: '12px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 15 },
  btnSecondary: { padding: '8px 16px', background: '#334155', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  btnDanger: { padding: '12px 24px', background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 15 },
  btnSuccess: { padding: '10px 20px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  toggleBtn: { padding: '6px 14px', borderRadius: 8, background: 'transparent', border: '1px solid', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

// QR_LIFETIME: WhatsApp QR codes expire after ~20 seconds
const QR_LIFETIME = 20;

function WhatsAppPanel({ waStatus, qrCode, qrTimestamp, pairingCode, globalBot, onToggleGlobal, onInit, onLogout }) {
  const [loginMode, setLoginMode] = useState('qr'); // 'qr' | 'phone'
  const [phoneInput, setPhoneInput] = useState('');
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingError, setPairingError] = useState('');
  const [countdown, setCountdown] = useState(null);

  // QR countdown timer
  useEffect(() => {
    if (waStatus !== 'qr_ready' || !qrTimestamp) { setCountdown(null); return; }
    const tick = () => {
      const elapsed = Math.floor((Date.now() - qrTimestamp) / 1000);
      const remaining = QR_LIFETIME - elapsed;
      setCountdown(remaining > 0 ? remaining : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [waStatus, qrTimestamp]);

  // Stop loading spinner once pairing code arrives via socket
  useEffect(() => {
    if (pairingCode) { setPairingLoading(false); setPairingError(''); }
  }, [pairingCode]);

  async function startPhonePairing() {
    if (!phoneInput.trim()) return;
    setPairingLoading(true);
    setPairingError('');
    try {
      await api.post('/api/whatsapp/pair', { phoneNumber: phoneInput.trim() });
      // Code will arrive via socket wa_pair_code → pairingCode prop
    } catch (err) {
      setPairingError(err.response?.data?.error || 'Failed to start pairing');
      setPairingLoading(false);
    }
  }

  // Login mode toggle — shown whenever not yet connected
  const loginTabs = (waStatus === 'disconnected' || waStatus === 'qr_ready') && (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      <button
        onClick={() => { setLoginMode('qr'); setPairingError(''); }}
        style={{ ...s.toggleBtn, borderColor: loginMode === 'qr' ? '#6366f1' : '#334155', color: loginMode === 'qr' ? '#6366f1' : '#94a3b8', background: loginMode === 'qr' ? '#6366f120' : 'transparent' }}
      >
        QR Code
      </button>
      <button
        onClick={() => setLoginMode('phone')}
        style={{ ...s.toggleBtn, borderColor: loginMode === 'phone' ? '#6366f1' : '#334155', color: loginMode === 'phone' ? '#6366f1' : '#94a3b8', background: loginMode === 'phone' ? '#6366f120' : 'transparent' }}
      >
        Phone Number
      </button>
    </div>
  );

  // Phone number panel — shared between disconnected + qr_ready states
  const phonePairingPanel = (
    <div style={{ width: '100%', maxWidth: 360 }}>
      <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 12 }}>
        Enter your WhatsApp number with country code (e.g. <strong style={{ color: '#cbd5e1' }}>919876543210</strong>).
        Then enter the 8-character code in WhatsApp → Linked Devices → Link with phone number.
      </p>
      <input
        type="tel"
        style={{ ...s.input, marginBottom: 8 }}
        placeholder="e.g. 919876543210"
        value={phoneInput}
        onChange={e => setPhoneInput(e.target.value.replace(/\D/g, ''))}
      />
      <button
        onClick={startPhonePairing}
        disabled={pairingLoading || !phoneInput.trim()}
        style={{ ...s.btnPrimary, width: '100%', opacity: pairingLoading || !phoneInput.trim() ? 0.6 : 1 }}
      >
        {pairingLoading ? 'Starting Pairing...' : 'Get Pairing Code'}
      </button>
      {pairingError && (
        <p style={{ color: '#ef4444', fontSize: 13, marginTop: 10 }}>{pairingError}</p>
      )}
      {pairingCode && (
        <div style={{ marginTop: 16, padding: 20, background: '#1e293b', borderRadius: 10, border: '1px solid #22c55e', textAlign: 'center' }}>
          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 8 }}>Enter this code in WhatsApp:</p>
          <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: 6, color: '#22c55e', fontFamily: 'monospace' }}>
            {pairingCode}
          </div>
          <p style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>WhatsApp → Linked Devices → Link with phone number</p>
        </div>
      )}
    </div>
  );

  return (
    <div style={s.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={s.sectionTitle}>WhatsApp Connection</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, color: globalBot ? '#22c55e' : '#ec4899', fontWeight: 600 }}>
            AI Bot: {globalBot ? 'Active (Auto-Replying)' : 'Paused Globally'}
          </span>
          <button
            onClick={onToggleGlobal}
            style={{ ...s.toggleBtn, borderColor: globalBot ? '#ec4899' : '#22c55e', color: globalBot ? '#ec4899' : '#22c55e' }}
          >
            {globalBot ? 'Pause AI' : 'Resume AI'}
          </button>
          {waStatus === 'ready' && (
            <button onClick={onLogout} style={{ ...s.toggleBtn, borderColor: '#ef4444', color: '#ef4444' }}>
              Disconnect WhatsApp
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16, padding: 16, background: '#0f172a', borderRadius: 8, border: '1px solid #334155' }}>
        {waStatus === 'qr_ready' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
            {loginTabs}
            {loginMode === 'qr' ? (
              qrCode ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', padding: 24, borderRadius: 8, width: 'fit-content' }}>
                  <QRCodeSVG value={qrCode.trim()} size={280} bgColor="#ffffff" fgColor="#000000" level="L" includeMargin={true} />
                  <p style={{ color: '#000', marginTop: 10, fontWeight: 700, fontSize: 14 }}>Scan with WhatsApp on your phone</p>
                  {countdown !== null && (
                    <div style={{ marginTop: 6, fontSize: 13, color: countdown <= 5 ? '#ef4444' : '#64748b' }}>
                      {countdown > 0 ? `Refreshes in ${countdown}s` : 'Refreshing QR...'}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#facc15', fontWeight: 600 }}>Generating QR code...</div>
              )
            ) : phonePairingPanel}
          </div>
        ) : waStatus === 'ready' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#22c55e', fontWeight: 600 }}>
            <span style={{ fontSize: 20 }}>✅</span> WhatsApp is Live and Connected.
          </div>
        ) : waStatus === 'disconnected' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ef4444', fontWeight: 600 }}>
              <span>⚠️</span> WhatsApp is disconnected.
            </div>
            {loginTabs}
            {loginMode === 'qr' ? (
              <button onClick={() => onInit('qr')} style={{ ...s.btnPrimary, alignSelf: 'flex-start' }}>Connect WhatsApp — Scan QR</button>
            ) : (
              <div style={{ ...s.section, background: '#0f172a', padding: 20, borderStyle: 'dashed' }}>
                <h3 style={{ ...s.sectionTitle, fontSize: 16 }}>Phone Number Pairing</h3>
                {phonePairingPanel}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#facc15', fontWeight: 600 }}>
            <span>🔄</span> Connecting to WhatsApp Web...
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Org Admin View ───────────────────────────────────────────────────────────
function OrgAdminBotPanel() {
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { loadBots(); }, []);

  async function loadBots() {
    try {
      const { data } = await api.get('/api/bot-config');
      setBots(data);
      if (data.length > 0 && !selectedBotId) selectBot(data[0]);
    } catch {}
  }

  function selectBot(bot) { setSelectedBotId(bot._id); setConfig({ ...bot }); setSuccessMsg(''); }

  async function createNewBot() {
    try {
      const { data } = await api.post('/api/bot-config', { profileName: 'New Bot Profile', botName: 'Alex', tone: 'Friendly and professional.', knowledgeBase: '', customPrompt: '' });
      setBots(prev => [data, ...prev]);
      selectBot(data);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to create bot');
    }
  }

  async function saveConfig() {
    if (!config || !selectedBotId) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/api/bot-config/${selectedBotId}`, config);
      setBots(prev => prev.map(b => b._id === selectedBotId ? data : b));
      setConfig(data);
      flash('Bot saved successfully!');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save configuration');
    }
    setSaving(false);
  }

  async function activateForMe() {
    if (!selectedBotId) return;
    try {
      const { data } = await api.post(`/api/bot-config/${selectedBotId}/activate`);
      setBots(prev => prev.map(b => ({ ...b, isMyActive: b._id === selectedBotId })));
      setConfig(data);
      flash('This bot is now YOUR active bot!');
    } catch {
      alert('Failed to activate bot');
    }
  }

  async function deleteBot() {
    if (!selectedBotId) return;
    if (!window.confirm('Delete this bot? Users who had it selected will lose their active bot.')) return;
    try {
      await api.delete(`/api/bot-config/${selectedBotId}`);
      const remaining = bots.filter(b => b._id !== selectedBotId);
      setBots(remaining);
      if (remaining.length > 0) selectBot(remaining[0]);
      else { setSelectedBotId(null); setConfig(null); }
    } catch {
      alert('Failed to delete bot');
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!config._id) return alert('Save the bot first before uploading a document.');
    setUploading(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('botId', config._id);
    try {
      const { data } = await api.post('/api/bot-config/upload', formData);
      setConfig(prev => ({
        ...prev,
        hasVectorKnowledge: data.vectorized,
        uploadedFiles: [...(prev.uploadedFiles || []), data.file],
      }));
      flash(data.message || 'Document uploaded');
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function deleteFile(idx) {
    if (!window.confirm('Remove this document from the bot\'s memory?')) return;
    try {
      const { data } = await api.delete(`/api/bot-config/${config._id}/documents/${idx}`);
      setConfig(prev => ({ ...prev, uploadedFiles: data.uploadedFiles, hasVectorKnowledge: data.uploadedFiles.length > 0 }));
      flash('Document removed');
    } catch {
      alert('Failed to remove document');
    }
  }

  async function clearDocuments() {
    if (!window.confirm("Delete all uploaded documents from this bot's memory?")) return;
    try {
      const { data } = await api.delete(`/api/bot-config/${config._id}/documents`);
      setConfig(prev => ({ ...prev, uploadedFiles: [], hasVectorKnowledge: false }));
      flash(data.message || 'Documents cleared');
    } catch {
      alert('Failed to clear documents');
    }
  }

  function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function flash(msg) { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3500); }

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ ...s.section, width: 300, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ ...s.sectionTitle, margin: 0 }}>Org Bot Library</h2>
          <button onClick={createNewBot} style={{ ...s.btnSecondary, padding: '6px 10px', fontSize: 12 }}>+ New Bot</button>
        </div>
        <p style={{ color: '#64748b', fontSize: 12, marginTop: 0, marginBottom: 12 }}>
          These bots are shared across all users in your org. Each user picks one to run on their WhatsApp.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflowY: 'auto' }}>
          {bots.length === 0 && <span style={{ color: '#94a3b8', fontSize: 13 }}>No bots yet. Create one above.</span>}
          {bots.map(b => (
            <div key={b._id} onClick={() => selectBot(b)} style={{ padding: '12px 16px', borderRadius: 8, cursor: 'pointer', background: selectedBotId === b._id ? '#334155' : '#0f172a', border: `1px solid ${b.isMyActive ? '#22c55e' : selectedBotId === b._id ? '#6366f1' : '#1e293b'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#f8fafc' }}>{b.profileName}</span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Alias: {b.botName}</span>
              </div>
              {b.isMyActive && <span style={{ fontSize: 10, background: '#22c55e', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>MY BOT</span>}
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...s.section, flex: 1, minWidth: 400 }}>
        {config ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, borderBottom: '1px solid #334155', paddingBottom: 16 }}>
              <div>
                <h2 style={{ ...s.sectionTitle, margin: '0 0 4px' }}>Edit Bot Profile</h2>
                <span style={{ fontSize: 12, color: '#64748b' }}>Changes apply to all users who have this bot selected.</span>
              </div>
              {!config.isMyActive
                ? <button style={s.btnSuccess} onClick={activateForMe}>Set as My Active Bot</button>
                : <span style={{ fontSize: 13, color: '#22c55e', fontWeight: 600 }}>✓ This is your active bot</span>
              }
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Profile Name (Internal)</label>
                <input type="text" style={s.input} value={config.profileName || ''} onChange={e => setConfig({ ...config, profileName: e.target.value })} placeholder="e.g. Sales Q4 Bot" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Bot Identity Name</label>
                <input type="text" style={s.input} value={config.botName || ''} onChange={e => setConfig({ ...config, botName: e.target.value })} placeholder="e.g. Alex" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Agent Category</label>
                <select
                  style={s.input}
                  value={config.activeTemplate || 'admissions'}
                  onChange={e => setConfig({ ...config, activeTemplate: e.target.value })}
                >
                  <option value="admissions">Admissions</option>
                  <option value="sales">Sales</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Response Language</label>
                <select
                  style={s.input}
                  value={config.language || 'auto'}
                  onChange={e => setConfig({ ...config, language: e.target.value })}
                >
                  <option value="auto">Auto-detect (mirrors user)</option>
                  <option value="english">English only</option>
                  <option value="hindi">Hindi</option>
                  <option value="telugu">Telugu</option>
                  <option value="tamil">Tamil</option>
                  <option value="marathi">Marathi</option>
                  <option value="kannada">Kannada</option>
                  <option value="bengali">Bengali</option>
                  <option value="gujarati">Gujarati</option>
                  <option value="punjabi">Punjabi</option>
                </select>
              </div>
            </div>

            <label style={s.label}>Persona Tone & Behavioral Rules</label>
            <textarea style={{ ...s.input, height: 80, resize: 'vertical' }} value={config.tone || ''} onChange={e => setConfig({ ...config, tone: e.target.value })} placeholder="e.g. You are a professional and empathetic counselor..." />

            {/* ── Document Library ── */}
            <div style={{ marginBottom: 20, padding: 16, background: '#0f172a', borderRadius: 10, border: '1px solid #334155' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ ...s.label, margin: 0 }}>Document Library</label>
                  {config.hasVectorKnowledge && (
                    <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, background: '#052e16', padding: '2px 8px', borderRadius: 10, border: '1px solid #22c55e44' }}>● Vector DB Active</span>
                  )}
                  {config.uploadedFiles?.length > 0 && (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{config.uploadedFiles.length} file{config.uploadedFiles.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {config.uploadedFiles?.length > 0 && (
                    <button onClick={clearDocuments} style={{ ...s.btnSecondary, fontSize: 12, padding: '5px 12px', color: '#ef4444' }}>
                      Clear All
                    </button>
                  )}
                  <input type="file" accept=".txt,.pdf" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileUpload} />
                  <button onClick={() => fileInputRef.current.click()} style={{ ...s.btnSecondary, fontSize: 12, padding: '5px 12px' }} disabled={uploading}>
                    {uploading ? '⏳ Indexing...' : '+ Upload PDF / TXT'}
                  </button>
                </div>
              </div>

              {(!config.uploadedFiles || config.uploadedFiles.length === 0) ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#475569', fontSize: 13, border: '1px dashed #334155', borderRadius: 8 }}>
                  No documents uploaded yet. Upload PDF or TXT files to give the bot a richer knowledge base.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {config.uploadedFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#1e293b', borderRadius: 8, border: '1px solid #334155' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 18 }}>📄</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            {formatBytes(f.size)}{f.chunkCount ? ` · ${f.chunkCount} chunks` : ''} · {f.uploadedAt ? new Date(f.uploadedAt).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteFile(i)}
                        style={{ background: 'transparent', border: '1px solid #ef444444', color: '#ef4444', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, flexShrink: 0, marginLeft: 12 }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Manual Knowledge Base ── */}
            <label style={s.label}>Manual Notes / Knowledge Base</label>
            <p style={{ color: '#64748b', fontSize: 12, marginTop: -10, marginBottom: 8 }}>Type or paste FAQs, pricing, program details here. Used alongside uploaded documents.</p>
            <textarea style={{ ...s.input, height: 160, resize: 'vertical' }} value={config.knowledgeBase || ''} onChange={e => setConfig({ ...config, knowledgeBase: e.target.value })} placeholder="Paste FAQs, product details, pricing, programs info..." />

            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #334155' }}>
              <h3 style={{ margin: '0 0 12px', color: '#f1f5f9', fontSize: 15 }}>Advanced: Raw Prompt Override</h3>
              <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
                If set, this overrides Name, Tone, and Knowledgebase above. The AI follows only this prompt.
              </p>
              <textarea
                style={{ ...s.input, height: 160, resize: 'vertical', borderColor: config.customPrompt ? '#f97316' : '#475569' }}
                value={config.customPrompt || ''}
                onChange={e => setConfig({ ...config, customPrompt: e.target.value })}
                placeholder="e.g. You are a support agent. Always greet with 'Hi there!'..."
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <button style={s.btnDanger} onClick={deleteBot}>Delete Bot</button>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {successMsg && <span style={{ color: '#22c55e', fontWeight: 600, fontSize: 14 }}>{successMsg}</span>}
                <button style={s.btnPrimary} onClick={saveConfig} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
            Select a bot from the library or create a new one.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Regular User View ────────────────────────────────────────────────────────
function UserBotSelector() {
  const [bots, setBots] = useState([]);
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadBots(); }, []);

  async function loadBots() {
    setLoading(true);
    try {
      const { data } = await api.get('/api/bot-config');
      setBots(data);
    } catch {}
    setLoading(false);
  }

  async function selectBot(bot) {
    try {
      await api.post(`/api/bot-config/${bot._id}/activate`);
      setBots(prev => prev.map(b => ({ ...b, isMyActive: b._id === bot._id })));
      setSuccessMsg(`"${bot.profileName}" is now your active bot!`);
      setTimeout(() => setSuccessMsg(''), 3500);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to select bot');
    }
  }

  if (loading) return <div style={{ ...s.section, color: '#64748b' }}>Loading bots...</div>;

  return (
    <div style={s.section}>
      <h2 style={s.sectionTitle}>Select Your Active Bot</h2>
      <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
        Your organization admin has created the bots below. Select one — it will automatically run on your WhatsApp and reply to incoming messages.
      </p>
      {successMsg && <p style={{ color: '#22c55e', fontWeight: 600, marginBottom: 16 }}>{successMsg}</p>}
      {bots.length === 0 ? (
        <div style={{ padding: 24, background: '#0f172a', borderRadius: 8, color: '#64748b', textAlign: 'center' }}>
          No bots have been created by your org admin yet. Please contact your administrator.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bots.map(bot => (
            <div key={bot._id} style={{ padding: '20px 24px', borderRadius: 10, border: `1px solid ${bot.isMyActive ? '#22c55e' : '#334155'}`, background: bot.isMyActive ? '#0d2b1a' : '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#f8fafc' }}>{bot.profileName}</span>
                  {bot.isMyActive && <span style={{ fontSize: 11, background: '#22c55e', color: '#fff', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>ACTIVE</span>}
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>Alias: <strong style={{ color: '#cbd5e1' }}>{bot.botName}</strong></span>
                  {bot.tone && <span style={{ fontSize: 13, color: '#94a3b8' }}>Tone: <strong style={{ color: '#cbd5e1' }}>{bot.tone.substring(0, 60)}{bot.tone.length > 60 ? '...' : ''}</strong></span>}
                </div>
              </div>
              {!bot.isMyActive
                ? <button onClick={() => selectBot(bot)} style={s.btnPrimary}>Select This Bot</button>
                : <span style={{ fontSize: 22 }}>✅</span>
              }
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BotManagement() {
  const { waStatus, qrCode, qrTimestamp, pairingCode } = useSocket();
  const { user } = useAuth();
  const [globalBot, setGlobalBot] = useState(true);

  const isAdmin = user?.role === 'orgadmin' || user?.role === 'superadmin';

  useEffect(() => {
    api.get('/api/whatsapp/global-bot').then(({ data }) => setGlobalBot(data.active)).catch(() => {});
  }, []);

  async function toggleGlobalBot() {
    const next = !globalBot;
    setGlobalBot(next);
    await api.post('/api/whatsapp/global-bot', { active: next }).catch(() => setGlobalBot(!next));
  }

  async function initWhatsApp(mode = 'qr') {
    try {
      await api.post('/api/whatsapp/init');
    } catch (e) {
      alert('Failed to initialize: ' + (e.response?.data?.error || e.message));
    }
  }

  async function logoutWhatsApp() {
    if (!window.confirm('Disconnect your WhatsApp? You will need to scan the QR again.')) return;
    await api.post('/api/whatsapp/logout');
  }

  return (
    <div style={s.page}>
      <div>
        <h1 style={s.title}>Bot Management</h1>
        <p style={{ color: '#64748b', margin: '6px 0 0', fontSize: 14 }}>
          {isAdmin
            ? 'Create and manage org-level bots. Each user in your org will select one to run on their WhatsApp.'
            : 'Scan your WhatsApp QR and select which bot you want running on your account.'}
        </p>
      </div>

      <WhatsAppPanel
        waStatus={waStatus}
        qrCode={qrCode}
        qrTimestamp={qrTimestamp}
        pairingCode={pairingCode}
        globalBot={globalBot}
        onToggleGlobal={toggleGlobalBot}
        onInit={initWhatsApp}
        onLogout={logoutWhatsApp}
      />

      {isAdmin && <OrgAdminBotPanel />}
      {!isAdmin && <UserBotSelector />}
    </div>
  );
}
